import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isPlainClick, withBasePath } from '@/lib/nav/list-state';
import { useReturnTarget, type ReturnOrigin } from '@/lib/nav/return-to';
import { Button } from '@/components/ui/button';

/**
 * The back link on a detail screen.
 *
 * It points at the view the operator actually came from — the withdrawal queue on the In-flight
 * tab, History filtered to rejected, the review queue as they had sorted it — rather than at the
 * top of one screen chosen when the page was written. `return-to.ts` decides where that is; this
 * renders it.
 *
 * A real `<a href>` rather than a button, because it is a link: middle-click and
 * open-in-new-tab must work, and a screen reader must announce it as a link. The click handler
 * only takes over the plain left-click, which is the one that should stay inside the app.
 */

/** Props for {@link BackLink}. */
export interface BackLinkProps {
  /** Where to go when the URL carries no return address — the screen this detail belongs to. */
  fallback: ReturnOrigin;
}

/** A link back to the screen this one was opened from. */
export function BackLink({ fallback }: BackLinkProps): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const target = useReturnTarget(fallback);

  return (
    <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
      <a
        href={withBasePath(target.href)}
        onClick={(event) => {
          if (!isPlainClick(event)) return;
          event.preventDefault();
          void navigate({ href: target.href });
        }}
      >
        <ArrowLeft aria-hidden="true" />
        {/* `?? {}` rather than passing through: under `exactOptionalPropertyTypes`, i18next's
            overloads read a bare `undefined` here as the positional default-value argument. */}
        {t(target.labelKey, target.labelParams ?? {})}
      </a>
    </Button>
  );
}
